/**
 * A escolha explícita para cada CCB que já tem caso, no Conferir envio (E165.F1.T06, GUS-105,
 * ADR-0114 §2; HTML 6.8): um `<fieldset>` por CCB, com o caso e se ele está aberto ou fechado, a
 * pergunta, as opções cabíveis e "Consultar caso existente". Nada é definitivo antes da escolha:
 * "Concluir envio" espera todas. Desde a E177.F2 (GUS-253 A2), a CCB já recebida (os mesmos
 * arquivos) também pergunta, com o texto dela.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { ContratoDoEnvioData, EscolhaDoContrato } from '@/lib/abertura_do_caso';

import { escolhaDaCcb, opcoesDaCcb, type EscolhasDoEnvio } from './escolhaDaCcb';
import { rotuloDoContrato } from './rotulosDoEnvio';

const t = ptBR.abertura.conferirEnvio.escolha;

function rotuloDaOpcao(opcao: EscolhaDoContrato, fechado: boolean): string {
  if (opcao === 'link') return t.link;
  return fechado ? t.newFechado : t.new;
}

function perguntaDaCcb(contrato: ContratoDoEnvioData, fechado: boolean): string {
  if (fechado) return t.perguntaFechado;
  return contrato.situacao === 'ja_recebido' ? t.perguntaJaRecebida : t.perguntaAberto;
}

function EscolhaDaCcb(props: {
  contrato: ContratoDoEnvioData;
  caso: { case_id: string; codigo: string; fechado?: boolean };
  escolhas: EscolhasDoEnvio;
  onEscolher: (chave: string, escolha: EscolhaDoContrato) => void;
  onAbrirCaso: (caseId: string) => void;
  disabled: boolean;
}): JSX.Element {
  const { contrato, caso } = props;
  const fechado = caso.fechado === true;
  const escolhida = escolhaDaCcb(contrato, props.escolhas);
  const legenda = (fechado ? t.legendaFechado : t.legendaAberto)
    .replace('{ccb}', rotuloDoContrato(contrato))
    .replace('{codigo}', caso.codigo);
  return (
    <fieldset
      className="my-5 border border-border-strong p-[18px]"
      data-testid={`conferir-escolha-${contrato.chave}`}
    >
      <legend className="px-2 font-semibold text-fg">{legenda}</legend>
      <p className="text-[14px] text-fg">{perguntaDaCcb(contrato, fechado)}</p>
      {opcoesDaCcb(contrato).map((opcao) => (
        <label
          key={opcao}
          className="my-[14px] flex cursor-pointer items-center gap-[10px] text-[14px] text-fg"
        >
          <input
            type="radio"
            name={`escolha-${contrato.chave}`}
            value={opcao}
            checked={escolhida === opcao}
            disabled={props.disabled}
            onChange={() => props.onEscolher(contrato.chave, opcao)}
            className="h-4 w-4 accent-cyan"
            data-testid={`conferir-escolha-${contrato.chave}-${opcao}`}
          />
          {rotuloDaOpcao(opcao, fechado)}
        </label>
      ))}
      <button
        type="button"
        className="mt-[6px] text-[14px] text-accent-700 underline underline-offset-2"
        data-testid={`conferir-consultar-${caso.case_id}`}
        onClick={() => props.onAbrirCaso(caso.case_id)}
      >
        {t.consultar}
      </button>
    </fieldset>
  );
}

export interface EscolhasDasCcbsProps {
  contratos: readonly ContratoDoEnvioData[];
  escolhas: EscolhasDoEnvio;
  onEscolher: (chave: string, escolha: EscolhaDoContrato) => void;
  onAbrirCaso: (caseId: string) => void;
  disabled?: boolean;
}

export function EscolhasDasCcbs(props: EscolhasDasCcbsProps): JSX.Element | null {
  const comEscolha = props.contratos.filter((c) => c.caso !== null && opcoesDaCcb(c).length > 0);
  if (comEscolha.length === 0) return null;
  return (
    <div data-testid="conferir-escolhas">
      {comEscolha.map((contrato) =>
        contrato.caso === null ? null : (
          <EscolhaDaCcb
            key={contrato.chave}
            contrato={contrato}
            caso={contrato.caso}
            escolhas={props.escolhas}
            onEscolher={props.onEscolher}
            onAbrirCaso={props.onAbrirCaso}
            disabled={props.disabled ?? false}
          />
        ),
      )}
    </div>
  );
}
